// TabControl.tsx
import React, { useState } from 'react';
import ControlRenderer, { Control } from '../ControlRenderer';
import { Tabs, Tab, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import MenuIcon from '@mui/icons-material/ListAlt';
import LeftIcon from '@mui/icons-material/ArrowLeft';
import RightIcon from '@mui/icons-material/ArrowRight';
import { FormModeType } from '@/components/FormDataProvider';
import { set } from '../utils/Utils';
import { useAuth } from '@/components/AuthProvider';
import ControlEditor from '../ControlEditor';

interface TabControlProps {
  control: Control;
  data: any;
  onDataChange: (newData: any) => void;
  mode: FormModeType;
  onParentMenuClick?: (event: React.MouseEvent<HTMLElement>) => void; // called when a parent menu is clicked
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
}

const TabControl: React.FC<TabControlProps> = ({ control, data, onDataChange, mode, onParentMenuClick, onComplete, onError, onClose }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuAnchorElAdd, setMenuAnchorElAdd] = React.useState<null | HTMLElement>(null);
  const [currentControl, setCurrentControl] = React.useState<Control | null>(null);
  const [editControl, setEditControl] = React.useState<Control | null>(null);
  const { authorize } = useAuth();

  const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorElAdd(event.currentTarget);
  };

  const handleSubmenuClose = () => {
    setMenuAnchorElAdd(null);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, control: Control) => {
    event.stopPropagation();
    setCurrentControl(control);
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setCurrentControl(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);
  };

  const handleEditControl = (controlToEdit: Control) => {
    setEditControl(controlToEdit);
  };

  const handleRemoveTab = (controlToRemove: Control) => {
    // Remove the control from the grid's controls
    const updatedControls = control.controls?.filter((c: Control) => c !== controlToRemove);

    // Update the grid control's controls
    // Assuming you have a setControls function or similar to update state
    set(control, 'controls', updatedControls);
  };

  const handleInsertTab = (control: Control, toLeft: boolean) => {

  };

  const isVisibleTab = (currentControl: Control) => {
    // we show all controls in design mode
    if (mode !== "design" && currentControl.visibility?.requiredRoles && currentControl.visibility.requiredRoles.length > 0 && !authorize(currentControl.visibility.requiredRoles)) {
      return false;
    }
    return true;
  };

  const visibleTabs = control.controls?.filter(c => isVisibleTab(c));

  return (
    <div>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        className="eform-tab-header"
        sx={{
          '& .MuiTabs-flexContainer': {
            justifyContent: 'center',
          },
        }}
      >
        {visibleTabs?.map((tab, index) => (
          <Tab
            key={index}
            label={
              <div style={{ display: "flex", alignItems: "center" }}>
                {mode === "design" && (
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      handleMenuOpen(event, tab);
                    }} // Bind the current tab index
                    style={{
                      padding: 0,
                      color: "gray",
                      zIndex: 1000,
                      marginRight: -10,
                      marginTop: -20,
                    }}
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                )}
                <Typography variant="body1" style={{ marginRight: "5px" }}>
                  {tab.label}
                </Typography>
              </div>
            }
            className="eform-tab"
            onClick={(event) => handleTabChange(event, index)}
          /* style={{
          padding: '10px',
          cursor: 'pointer',
          border: '1px solid #ccc',
          borderBottom: index === activeTab ? 'none' : '1px solid #ccc',
          background: index === activeTab ? '#f0f0f0' : 'white',
        }} */
          />
        ))}
      </Tabs>
      <div style={{ padding: "10px" }} className="eform-tab">
        {visibleTabs && visibleTabs[activeTab] && (
          <ControlRenderer
            control={visibleTabs[activeTab]}
            data={data}
            onDataChange={onDataChange}
            mode={mode}
            onComplete={onComplete}
            onError={onError}
            onClose={onClose}
          />
        )}
      </div>
      {currentControl && (
        <Menu
          id="control-menu"
          disableScrollLock
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {onParentMenuClick && (
            <MenuItem
              onClick={(event) => {
                onParentMenuClick(event);
              }}
            >
              <MenuIcon className="eform-menu-icon" />
              <Typography variant="inherit">Parent Control</Typography>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              handleEditControl(currentControl);
              handleMenuClose();
            }}
          >
            <EditIcon className="eform-menu-icon" style={{ color: "green" }} />
            <Typography variant="inherit">Edit TabPage Properties</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleInsertTab(currentControl, true);
              handleMenuClose();
            }}
          >
            <LeftIcon className="eform-menu-icon" />
            <Typography variant="inherit">Insert Tab Left</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleInsertTab(currentControl, false);
              handleMenuClose();
            }}
          >
            <RightIcon className="eform-menu-icon" />
            <Typography variant="inherit">Insert Tab Right</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleRemoveTab(currentControl);
              handleMenuClose();
            }}
          >
            <RemoveIcon className="eform-menu-icon" style={{ color: "red" }} />
            <Typography variant="inherit">Remove Tab</Typography>
          </MenuItem>
          {/* Add more MenuItems as needed */}
        </Menu>
      )}
      {editControl && (
        <ControlEditor
          controlToEdit={editControl}
          data={data}
          dataKey={control.dataKey}
          onComplete={() => {
            setEditControl(null);
          }}
          onError={() => setEditControl(null)}
        />
      )}
    </div>
  );
};

export default TabControl;
